import { z } from 'zod';

/**
 * Primitives shared by every REST DTO and WebSocket message.
 *
 * Keep this file free of domain concepts — it is imported by both halves of the
 * contract surface and by the dashboard bundle.
 */

/** Database primary keys are CUIDs generated by Prisma. */
export const idSchema = z.string().min(8).max(64);
export type Id = z.infer<typeof idSchema>;

/**
 * ULIDs are used where the *client* mints the identifier — batch ids and command
 * ids — because they sort lexicographically by creation time, which makes an
 * out-of-order resend obvious in logs.
 */
export const ulidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'must be a ULID');
export type Ulid = z.infer<typeof ulidSchema>;

/** ISO-8601 timestamp. Everything on the wire is a string, never a Date. */
export const isoDateTimeSchema = z.string().datetime({ offset: true });
export type IsoDateTime = z.infer<typeof isoDateTimeSchema>;

/** Lenient variant for values parsed out of a page, where the format varies. */
export const looseDateTimeSchema = z.union([isoDateTimeSchema, z.string().datetime()]);

export const emailSchema = z.string().email().max(254).toLowerCase().trim();

/**
 * Password policy. Length carries most of the strength here; scrypt handles the
 * rest. A composition rule ("must contain a symbol") mostly pushes users toward
 * predictable substitutions, so we require length and reject the obvious.
 */
export const passwordSchema = z
  .string()
  .min(10, 'Mật khẩu phải có ít nhất 10 ký tự')
  .max(200, 'Mật khẩu quá dài');

// ---------------------------------------------------------------------------
// Scores
// ---------------------------------------------------------------------------

/**
 * Internal score. EVERY score inside the system is on this 0-100 scale —
 * rule, AI, intent, freshness and final alike.
 */
export const score100Schema = z.number().min(0).max(100);

/** Display-only score, derived once at the very end for the dashboard. */
export const score10Schema = z.number().min(0).max(10);

/** The one place a 0-100 score becomes the 0.0-10.0 value users see. */
export function toDisplayScore(score100: number): number {
  return Math.round(score100) / 10;
}

/** Convert a model's 1-10 relevance rating into the internal 0-100 scale. */
export function relevance10ToScore100(relevance: number): number {
  const clamped = Math.min(10, Math.max(1, relevance));
  return Math.round(((clamped - 1) / 9) * 100);
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export const paginationMetaSchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0),
});
export type PaginationMeta = z.infer<typeof paginationMetaSchema>;

/** Wrap an item schema into `{ items, meta }`. */
export function paginated<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    meta: paginationMetaSchema,
  });
}

export interface Paginated<T> {
  items: T[];
  meta: PaginationMeta;
}

export function buildPaginationMeta(
  page: number,
  pageSize: number,
  total: number,
): PaginationMeta {
  return { page, pageSize, total, totalPages: Math.ceil(total / pageSize) };
}

export const sortOrderSchema = z.enum(['asc', 'desc']).default('desc');
export type SortOrder = z.infer<typeof sortOrderSchema>;

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Machine-readable error codes the dashboard branches on. */
export const API_ERROR_CODES = [
  'VALIDATION_ERROR',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'SUBSCRIPTION_LIMIT',
  'AGENT_OFFLINE',
  'INVALID_STATE_TRANSITION',
  'EMERGENCY_STOP_ACTIVE',
  'POLICY_REJECTED',
  'UPSTREAM_ERROR',
  'INTERNAL_ERROR',
] as const;
export const apiErrorCodeSchema = z.enum(API_ERROR_CODES);
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export const apiErrorSchema = z.object({
  statusCode: z.number().int(),
  code: apiErrorCodeSchema,
  message: z.string(),
  /** Field-level validation detail, keyed by dotted path. */
  details: z.record(z.string(), z.array(z.string())).optional(),
  requestId: z.string().optional(),
  timestamp: isoDateTimeSchema,
});
export type ApiError = z.infer<typeof apiErrorSchema>;

export const okResponseSchema = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof okResponseSchema>;

// ---------------------------------------------------------------------------
// Versioning
// ---------------------------------------------------------------------------

/**
 * Every configuration snapshot the server pushes carries these three numbers.
 * The agent applies a snapshot only when something actually changed, which is
 * what makes redelivery after a reconnect harmless.
 */
export const configVersionSchema = z.object({
  version: z.number().int().min(0),
  /** Interpreter generation of the agent build — one number for the whole
   *  agent, whatever platforms it drives; packs gate on it per document. */
  adapterVersion: z.number().int().min(1),
  selectorPackVersion: z.number().int().min(0),
});
export type ConfigVersion = z.infer<typeof configVersionSchema>;

// ---------------------------------------------------------------------------
// Inline images
// ---------------------------------------------------------------------------

/**
 * Image formats every consumer in this system agrees on.
 *
 * Both AI providers accept these; the agent only ever produces the first. The
 * list is here rather than duplicated per provider so a format that one accepts
 * and another rejects cannot be introduced by editing one file.
 */
export const INLINE_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const inlineImageMimeTypeSchema = z.enum(INLINE_IMAGE_MIME_TYPES);
export type InlineImageMimeType = (typeof INLINE_IMAGE_MIME_TYPES)[number];

/**
 * Decoded ceiling for an image that travels inline, in bytes.
 *
 * 256 KB is roughly forty times what a cropped timestamp label actually weighs
 * (a 320×48 PNG of text is 3-6 KB), and that margin is the point: the number is
 * a guard against a bug — a crop that accidentally captured the whole viewport,
 * a screenshot of a video frame — not a budget anyone is expected to spend.
 *
 * The ceiling matters because these bytes do not go to object storage. They ride
 * inside a WebSocket frame and then inside a BullMQ payload in Redis, so an
 * unbounded one would be charged to the agent's socket, the API's memory and the
 * queue's memory at once. Both providers allow far larger images; this limit is
 * ours, and it is about transport, not about what a model can read.
 */
export const INLINE_IMAGE_MAX_BYTES = 256 * 1024;

/**
 * The same ceiling expressed in base64 characters, which is what the schema can
 * actually measure. Base64 is 4 characters per 3 bytes, plus padding.
 */
export const INLINE_IMAGE_MAX_BASE64_LENGTH = Math.ceil(INLINE_IMAGE_MAX_BYTES / 3) * 4;

/**
 * Standard base64 — no data URL prefix, no URL-safe alphabet.
 *
 * Checked with a pattern rather than by decoding, because this schema runs on
 * the API's hot ingestion path and in the browser: `Buffer.from(x, 'base64')`
 * silently ignores characters it does not understand, so a corrupted payload
 * would decode to *something* and be stored as a valid image. The pattern
 * refuses it instead.
 */
export const base64ImageDataSchema = z
  .string()
  .min(1)
  .max(INLINE_IMAGE_MAX_BASE64_LENGTH)
  .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'phải là base64 chuẩn, không kèm tiền tố data:');

/**
 * An image carried inside a message rather than uploaded to object storage.
 *
 * Deliberately not a `MediaObject`: those are durable, workspace-scoped, signed
 * -URL artefacts with a retention policy. This is a few kilobytes that exist to
 * answer one question and are discarded the moment it is answered. Giving it a
 * storage key, a row and a sweeper would be more machinery than the thing it
 * carries.
 */
export const inlineImageSchema = z.object({
  mimeType: inlineImageMimeTypeSchema,
  /** Base64, no `data:` prefix. */
  data: base64ImageDataSchema,
  /** Pixel size, for diagnostics — a 0×0 or full-page crop is a parser bug. */
  width: z.number().int().positive().max(10_000).optional(),
  height: z.number().int().positive().max(10_000).optional(),
});
export type InlineImage = z.infer<typeof inlineImageSchema>;
